/**
 * Signing-PIN helpers for the prescriber leg (Model A).
 *
 * A referring physician opens a signing link on their phone and enters a
 * 6-digit PIN they chose themselves at onboarding. The link proves possession
 * of the phone; the PIN is the second factor the phone does not contain.
 *
 * **Digest scheme — canonical in `med_form_app/backend/signer_pin.py`:**
 * lowercase-hex sha256 over the UTF-8 PIN string, no salt, no separator.
 * Identical to the single-factor path in `DobChallenge`. The PIN itself never
 * crosses the wire; only the digest does, and the server stores only a hash.
 *
 * **The server is authoritative.** `validatePin` below mirrors the server's
 * weak-PIN rules so a physician standing in their office isn't bounced by a
 * round-trip, but it decides nothing: the setup endpoint re-checks every rule
 * and its 422 `detail` is what the setup page shows. If the two ever disagree,
 * the server wins and this file is the thing that is wrong.
 */

import { sha256Hex } from "./bridgeClient";

/** PINs are exactly this many digits. */
export const PIN_LENGTH = 6;

// Strictly-ascending and strictly-descending 6-digit runs. Enumerated rather
// than computed so this list reads as the same closed set the server checks.
const ASCENDING_RUNS = ["012345", "123456", "234567", "345678", "456789"];
const DESCENDING_RUNS = ["987654", "876543", "765432", "654321", "543210"];

/**
 * Verbatim from `signer_pin.validate_pin` — the same sentence the bridge
 * returns as its 422 `detail` for a weak digest. One wording, so a physician
 * who trips the local check and one who trips the server's read the same thing.
 */
const WEAK_PIN_MESSAGE =
  "That PIN is too easy to guess — avoid repeated digits (111111) and sequences (123456).";

/**
 * The digest the bridge compares against — see the scheme note above.
 * Takes the PIN exactly as typed (validatePin has already ruled out anything
 * with whitespace or non-digits, so there is nothing to normalize).
 */
export async function pinDigest(pin: string): Promise<string> {
  return sha256Hex(pin);
}

/**
 * Client mirror of the server's weak-PIN rules. Returns a human-readable
 * rejection reason, or null when the PIN is acceptable.
 *
 * The rule set is deliberately short and closed: exactly six digits, no
 * all-same-digit PIN, no strictly-ascending or strictly-descending run.
 * Nothing else — a longer client-side list would start refusing PINs the
 * server accepts, which is the same failure as accepting ones it refuses.
 *
 * The three messages are verbatim from `signer_pin.validate_pin`. Note the
 * asymmetry the server cannot escape: only a DIGEST crosses the wire, and a
 * digest carries no length, so "exactly six digits" is enforced HERE and
 * nowhere else. The weak set is enforced at both ends, by digest on the server.
 */
export function validatePin(pin: string): string | null {
  if (pin.length !== PIN_LENGTH) {
    return `Your PIN must be exactly ${PIN_LENGTH} digits.`;
  }
  if (!/^\d+$/.test(pin)) {
    return "Your PIN must be digits only.";
  }
  if (/^(\d)\1{5}$/.test(pin)) {
    return WEAK_PIN_MESSAGE;
  }
  if (ASCENDING_RUNS.includes(pin) || DESCENDING_RUNS.includes(pin)) {
    return WEAK_PIN_MESSAGE;
  }
  return null;
}
